import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Swords, Trophy, CalendarCheck, Flame } from "lucide-react";
import api, { apiErr } from "@/lib/api";

// Relaxing puzzle lineup for the lobby — ordered per owner. Unbuilt games are visible as "Coming soon".
const LINEUP = [
  { key: "solitaire", name: "Solitaire", desc: "Chain cards up or down. Calm, quick, classic.", to: "/duels?practice=solitaire_sprint", emoji: "🂡", hero: true },
  { key: "mahjong", name: "Mahjong Solitaire", desc: "Classic turtle layout, hint & shuffle. New!", to: "/casino/mahjong", emoji: "🀄" },
  { key: "wordvolt", name: "WordVolt", desc: "Seven letters, sixty seconds, your best words.", to: "/duels?practice=word_volt", emoji: "🔤", group: "Word games" },
  { key: "crossword", name: "Crossword", desc: "Daily grids, gentle clues.", emoji: "✏️", group: "Word games" },
  { key: "wordsearch", name: "Word Search", desc: "Find every hidden word.", emoji: "🔍", group: "Word games" },
  { key: "sudoku", name: "Sudoku", desc: "Easy to expert, one number at a time.", emoji: "🔢" },
  { key: "checkers", name: "Checkers", desc: "The board game you grew up with.", emoji: "⚪" },
  { key: "dominoes", name: "Dominoes", desc: "Match the pips, clear your hand.", emoji: "🁫" },
  { key: "chess", name: "Chess Puzzles", desc: "Find the best move in a few seconds.", emoji: "♞" },
  { key: "2048", name: "2048 Puzzle", desc: "Slide, merge, relax.", emoji: "🧩" },
];

export default function PuzzleLineup({ streak = 0 }) {
  const [reminder, setReminder] = useState(null);
  useEffect(() => { api.get("/mahjong/daily").then(({ data }) => setReminder(data.reminder)).catch(() => {}); }, []);
  const toggleReminder = async (opt_in) => {
    try { const { data } = await api.post("/mahjong/reminder", { opt_in }); setReminder(data.reminder); toast.success(opt_in ? "Evening reminder saved" : "Reminder turned off"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const Tile = ({ g, i }) => {
    const body = (
      <>
        <div className={`${g.hero ? "text-6xl" : "text-4xl"} mb-3`} aria-hidden>{g.emoji}</div>
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className={`font-display font-bold ${g.hero ? "text-2xl sm:text-3xl" : "text-lg"}`}>{g.name}</h3>
          {g.group && <span className="text-[10px] uppercase tracking-widest text-cyan-300/80">{g.group}</span>}
          {!g.to && <span data-testid={`coming-soon-${g.key}`} className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/10 text-gray-400">Coming soon</span>}
        </div>
        <p className="text-sm text-gray-400 mt-1">{g.desc}</p>
      </>
    );
    const cls = `glass rounded-2xl p-5 border ${g.hero ? "sm:col-span-2 lg:col-span-2 border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-purple-600/10" : "border-white/10"} ${g.to ? "hover:border-amber-400/50 hover:-translate-y-0.5 transition-all duration-200" : "opacity-70"}`;
    return g.to
      ? <Link to={g.to} data-testid={`puzzle-tile-${g.key}`} className={cls}>{body}</Link>
      : <div data-testid={`puzzle-tile-${g.key}`} aria-disabled="true" className={cls}>{body}</div>;
  };
  return (
    <section data-testid="puzzle-lineup" className="mb-10">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h2 className="font-display text-2xl sm:text-3xl font-bold">Relax &amp; play</h2>
        <div data-testid="daily-puzzle-card" className="ml-auto flex flex-col gap-1.5 rounded-xl bg-gradient-to-r from-amber-500/20 to-purple-600/20 border border-amber-500/30 px-4 py-2">
          <Link to="/casino/mahjong?daily=1" data-testid="daily-puzzle-link" className="flex items-center gap-2 text-sm font-semibold hover:text-amber-200">
            <CalendarCheck className="w-4 h-4 text-amber-300" /> Daily Puzzle
            <span className="flex items-center gap-1 text-xs text-amber-300"><Flame className="w-3.5 h-3.5" />{streak}-day streak</span>
          </Link>
          {reminder && (
            <label data-testid="lobby-reminder-label" className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
              <input data-testid="lobby-reminder-checkbox" type="checkbox" className="accent-amber-400" checked={!!reminder.opt_in} onChange={(e) => toggleReminder(e.target.checked)} />
              <span>Email reminder around 8 PM ET</span>
            </label>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {LINEUP.map((g, i) => <Tile key={g.key} g={g} i={i} />)}
      </div>
      <div data-testid="real-people-row" className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Link to="/duels" data-testid="lineup-duels" className="glass rounded-2xl p-5 border border-emerald-500/30 hover:border-emerald-400/60 transition-colors">
          <div className="flex items-center gap-2 mb-1"><Swords className="w-5 h-5 text-emerald-300" /><h3 className="font-display font-bold text-lg">Duels</h3></div>
          <p className="text-sm text-gray-300 font-semibold">Play real people, never bots.</p>
          <p className="text-xs text-gray-500 mt-1">Head-to-head on the same board or deal — both players get the identical puzzle from our fixed library.</p>
        </Link>
        <Link to="/tournaments" data-testid="lineup-tournaments" className="glass rounded-2xl p-5 border border-purple-500/30 hover:border-purple-400/60 transition-colors">
          <div className="flex items-center gap-2 mb-1"><Trophy className="w-5 h-5 text-purple-300" /><h3 className="font-display font-bold text-lg">Tournaments</h3></div>
          <p className="text-sm text-gray-300 font-semibold">Play real people, never bots.</p>
          <p className="text-xs text-gray-500 mt-1">Everyone plays the same puzzle; the leaderboard is just for bragging rights and Fun Coins.</p>
        </Link>
      </div>
    </section>
  );
}
